/**
 * DemoBanner Component
 * Persistent banner shown when user is in demo mode (like Stripe's test mode banner)
 * Should be displayed at the top of Dashboard, Budget, Chats, and other main screens.
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

interface DemoBannerProps {
  profileName: string | null;
  onImportReal?: () => void;
  onExitDemo?: () => void;
  compact?: boolean;
}

export default function DemoBanner({
  profileName,
  onImportReal,
  onExitDemo,
  compact = false,
}: DemoBannerProps) {
  if (compact) {
    return (
      <View style={styles.compactContainer}>
        <Icon name="flask-outline" size={14} color={styles.bannerText.color} />
        <Text style={styles.compactText}>
          MODE DEMO{profileName ? ` - ${profileName}` : ''}
        </Text>
        {onExitDemo && (
          <TouchableOpacity onPress={onExitDemo} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <Text style={styles.compactLink}>Quitter</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.leftSection}>
          <Icon name="flask-outline" size={18} color={styles.bannerText.color} />
          <View style={styles.textContainer}>
            <Text style={styles.bannerText}>
              MODE DEMO {profileName ? `- Donnees fictives de ${profileName}` : '- Donnees fictives'}
            </Text>
          </View>
        </View>
        <View style={styles.actions}>
          {onImportReal && (
            <TouchableOpacity style={styles.actionButton} onPress={onImportReal}>
              <Text style={styles.actionButtonText}>Importer mes releves</Text>
            </TouchableOpacity>
          )}
          {onExitDemo && (
            <TouchableOpacity onPress={onExitDemo} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Text style={styles.exitLink}>Quitter</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
}

const BANNER_BACKGROUND = '#FFF3E0'; // Light orange (like Stripe test mode)
const BANNER_TEXT = '#E65100'; // Dark orange
const BANNER_BORDER = '#FFB74D'; // Medium orange

const styles = StyleSheet.create({
  container: {
    backgroundColor: BANNER_BACKGROUND,
    borderBottomWidth: 1,
    borderBottomColor: BANNER_BORDER,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 200,
  },
  textContainer: {
    marginLeft: spacing.sm,
    flex: 1,
  },
  bannerText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold as '600',
    color: BANNER_TEXT,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  actionButton: {
    backgroundColor: BANNER_TEXT,
    borderRadius: 6,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  actionButtonText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold as '600',
    color: '#FFFFFF',
  },
  exitLink: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium as '500',
    color: BANNER_TEXT,
    textDecorationLine: 'underline',
  },

  // Compact version (40% of header zone height)
  compactContainer: {
    backgroundColor: BANNER_BACKGROUND,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm, // Increased for 40% proportion
    paddingHorizontal: spacing.sm,
    gap: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: BANNER_BORDER,
  },
  compactText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold as '700',
    color: BANNER_TEXT,
    letterSpacing: 0.5,
  },
  compactLink: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium as '500',
    color: BANNER_TEXT,
    textDecorationLine: 'underline',
    marginLeft: spacing.sm,
  },
});
